import React, { useState, useEffect, useMemo } from 'react';
import { ToolResultProps } from '../types';
import { getDocumentsInfo, generateDocumentUrl, DocumentInfo } from './documentHelpers';
import { Card, CardContent } from "../../../../../../../components/ui/card";
import { Button } from "../../../../../../../components/ui/button";
import { Skeleton } from "../../../../../../../components/ui/skeleton";
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from "../../../../../../../components/ui/collapsible";
import { CheckCircle2, ChevronRight, Info } from "lucide-react";
import { getIconPath } from '../../../../utils/documentStyles';

interface DocumentFromTool {
  document_id: string;
  document_name: string;
  document_type: string;
  content_length?: number;
  success: boolean;
}

const GetDocumentContentResult: React.FC<ToolResultProps> = ({
  thought,
  onDocumentClick
}) => {
  const { toolResult } = thought;
  const metadata = toolResult?.metadata || {};
  
  const normalizeDocuments = (): DocumentFromTool[] => {
    if (Array.isArray(metadata.documents) && metadata.documents.length > 0) {
      return metadata.documents.map((doc: any) => ({
        document_id: doc.document_id || doc.id,
        document_name: doc.document_name || doc.name,
        document_type: doc.document_type || 'document',
        content_length: doc.content_length,
        success: doc.success !== false
      }));
    }
    
    if (metadata.document_id) {
      return [{
        document_id: metadata.document_id,
        document_name: metadata.document_name || `Document ${metadata.document_id.substring(0, 8)}...`,
        document_type: metadata.document_type || 'document',
        content_length: metadata.content_length,
        success: metadata.success !== false
      }];
    }
    
    return [];
  };
  
  const documents: DocumentFromTool[] = normalizeDocuments();
  const totalFound = metadata.total_found || documents.length;
  const totalRequested = metadata.total_requested || documents.length;
  const success = toolResult?.success !== false;

  const [documentsInfo, setDocumentsInfo] = useState<DocumentInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [urlsMap, setUrlsMap] = useState<Map<string, string | null>>(new Map());
  
  const contentLengthMap = useMemo(() => {
    const map = new Map<string, number>();
    documents.forEach(doc => {
      if (doc.content_length !== undefined) {
        map.set(doc.document_id, doc.content_length);
      }
    });
    return map;
  }, [documents]);

  const documentIds = useMemo(() => {
    return documents.map(doc => doc.document_id);
  }, [documents]);

  useEffect(() => {
    const loadDocumentsInfo = async () => {
      if (documentIds.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const info = await getDocumentsInfo(documentIds);
        const combinedInfo = info.map((cachedDoc, index) => {
          const toolDoc = documents[index];
          return {
            id: cachedDoc.id,
            name: toolDoc?.document_name || cachedDoc.name || cachedDoc.id,
            pdf_url: cachedDoc.pdf_url,
            source_path: cachedDoc.source_path,
            document_type: cachedDoc.document_type
          };
        });
        
        setDocumentsInfo(combinedInfo);
        
        const urlsPromises = combinedInfo.map(async (doc) => {
          if (doc.source_path) {
            const url = await generateDocumentUrl(doc.source_path);
            return { id: doc.id, url };
          }
          return { id: doc.id, url: doc.pdf_url };
        });
        
        const urlsResults = await Promise.all(urlsPromises);
        const newUrlsMap = new Map<string, string | null>();
        urlsResults.forEach(({ id, url }) => {
          newUrlsMap.set(id, url);
        });
        setUrlsMap(newUrlsMap);
      } catch (err) {
        console.error('Error loading documents info:', err);
        setError('Error loading document info');
        setDocumentsInfo(documents.map(doc => ({
          id: doc.document_id,
          name: doc.document_name || `Document ${doc.document_id.substring(0, 8)}...`,
          pdf_url: null
        })));
      } finally {
        setIsLoading(false);
      }
    };

    loadDocumentsInfo();
  }, [documentIds.join(',')]);

  if (isLoading && documents.length > 0) {
    return (
      <div className="mt-1 space-y-1">
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
    );
  }

  const displayDocuments = documentsInfo.length > 0 
    ? documentsInfo
    : documents.map(doc => ({
        id: doc.document_id,
        name: doc.document_name || `Document ${doc.document_id.substring(0, 8)}...`,
        pdf_url: null,
        document_type: doc.document_type
      }));

  if (displayDocuments.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-1">

      <div className="space-y-1">
        {displayDocuments.map((doc) => {
          const url = urlsMap.get(doc.id) ?? doc.pdf_url;
          const hasUrl = !!url;
          const contentLength = contentLengthMap.get(doc.id);
          
          return (
            <Button
              key={doc.id}
              variant="ghost"
              size="sm"
              className="w-full justify-start h-8 py-1 px-2 hover:bg-muted/50 transition-colors group border border-transparent hover:border-border/40 rounded-md"
              disabled={!hasUrl}
              onClick={() => onDocumentClick?.(doc.id, url, doc.name)}
            >
              <div className="flex items-center gap-2 w-full">
                <img
                  src={getIconPath(doc.name)}
                  className="h-3.5 w-3.5 object-contain opacity-80 flex-shrink-0"
                  alt=""
                />
                <span className="text-[11px] font-normal truncate flex-1 text-foreground/80">
                  {doc.name}
                </span>
                {contentLength !== undefined && (
                  <span className="text-[9px] text-muted-foreground/60 tabular-nums">
                    {Math.round(contentLength / 1024)} KB
                  </span>
                )}
                {hasUrl && <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />}
              </div>
            </Button>
          );
        })}
      </div>

      {process.env.NODE_ENV === 'development' && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="h-5 py-0 px-1 text-[8px] text-muted-foreground/50 hover:text-muted-foreground w-fit gap-1">
              <Info className="h-2.5 w-2.5" />
              <span>Debug</span>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="mt-1 p-1.5 bg-muted/30 text-[9px] rounded font-mono overflow-auto max-h-24 border border-border/20">
              {JSON.stringify({ metadata, documents }, null, 2)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};

export default GetDocumentContentResult;
